/**
 * Billing Tab Wrapper
 * 
 * Wraps the BillingTab component with:
 * 1. Error Boundary - For crash isolation (a billing error won't kill the session)
 * 
 * NOTE: BillingProvider is now provided at the Dashboard level to support 
 * both the Billing Tab and Claims Workshop dashboard widgets.
 * 
 * Data is fetched on-demand when this tab is mounted.
 */

import React, { useEffect } from 'react';
import { useBilling } from '../../context/BillingContext';
import { BillingTab } from './BillingTab';

// Error Boundary for crash isolation
class BillingErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[Billing] Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            ⚠️ Billing Module Error
          </h3>
          <p className="text-red-600 mb-4">
            Something went wrong in the billing module. Your session is still active.
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
          <details className="mt-4 text-sm text-red-500">
            <summary>Error Details</summary>
            <pre className="mt-2 p-2 bg-red-100 rounded overflow-auto">
              {this.state.error?.toString()}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Inner component that uses the BillingContext
 */
const BillingTabContent = ({
  // Pass through props that come from App.js
  users,
  providers,
  clients,
  insurances,
  currentBatch,
  user,
  fetchCurrentBatch,
  fetchNoteForReview,
  setCurrentNoteIndex,
  setSelectedNoteForReview,
  setSelectedClaim,
  // Mar 2026 — hoisted sort state from App.js (see comment there).
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  onViewNote,
}) => {
  const {
    // From BillingContext
    claimsQueue,
    loadingClaims,
    notesReadyForBilling,
    loadingNotes,
    notesSentForCorrection,
    fetchNotesSentForCorrection,
    billingProviderFilter,
    setBillingProviderFilter,
    billingProviderFilterMode,
    setBillingProviderFilterMode,
    billingClientFilter,
    setBillingClientFilter,
    billingClientFilterMode,
    setBillingClientFilterMode,
    billingInsuranceFilter,
    setBillingInsuranceFilter,
    billingInsuranceFilterMode,
    setBillingInsuranceFilterMode,
    billingCptFilter,
    setBillingCptFilter,
    billingCptFilterMode,
    setBillingCptFilterMode,
    billingDateFrom,
    setBillingDateFrom,
    billingDateTo,
    setBillingDateTo,
    billingSearchTerm,
    setBillingSearchTerm,
    fetchClaimsQueue,
    fetchNotesForBilling,
    fetchClaimDetail,
    setNotesReadyForBilling,
    setNotesSentForCorrection,
  } = useBilling();

  // Fetch data on mount (on-demand loading)
  useEffect(() => {
    void 0 && console.log('[BillingTab] Mounted - fetching data on-demand');
    fetchClaimsQueue();
    fetchNotesForBilling();
    fetchNotesSentForCorrection();
  }, []);

  return (
    <BillingTab
      // Data from context
      claimsQueue={claimsQueue}
      notesReadyForBilling={notesReadyForBilling}
      notesSentForCorrection={notesSentForCorrection}
      loadingNotes={loadingNotes}
      loadingClaims={loadingClaims}
      billingProviderFilter={billingProviderFilter}
      setBillingProviderFilter={setBillingProviderFilter}
      billingProviderFilterMode={billingProviderFilterMode}
      setBillingProviderFilterMode={setBillingProviderFilterMode}
      billingClientFilter={billingClientFilter}
      setBillingClientFilter={setBillingClientFilter}
      billingClientFilterMode={billingClientFilterMode}
      setBillingClientFilterMode={setBillingClientFilterMode}
      billingInsuranceFilter={billingInsuranceFilter}
      setBillingInsuranceFilter={setBillingInsuranceFilter}
      billingInsuranceFilterMode={billingInsuranceFilterMode}
      setBillingInsuranceFilterMode={setBillingInsuranceFilterMode}
      billingCptFilter={billingCptFilter}
      setBillingCptFilter={setBillingCptFilter}
      billingCptFilterMode={billingCptFilterMode}
      setBillingCptFilterMode={setBillingCptFilterMode}
      billingDateFrom={billingDateFrom}
      setBillingDateFrom={setBillingDateFrom}
      billingDateTo={billingDateTo}
      setBillingDateTo={setBillingDateTo}
      billingSearchTerm={billingSearchTerm}
      setBillingSearchTerm={setBillingSearchTerm}
      fetchClaimsQueue={fetchClaimsQueue}
      fetchClaimDetail={fetchClaimDetail}
      // Pass-through props from App.js
      users={users}
      providers={providers}
      clients={clients}
      insurances={insurances}
      currentBatch={currentBatch}
      user={user}
      fetchCurrentBatch={fetchCurrentBatch}
      fetchNoteForReview={fetchNoteForReview}
      fetchNotesSentForCorrection={fetchNotesSentForCorrection}
      fetchNotesReadyForBilling={fetchNotesForBilling}
      setCurrentNoteIndex={setCurrentNoteIndex}
      setSelectedNoteForReview={setSelectedNoteForReview}
      setSelectedClaim={setSelectedClaim}
      setNotesReadyForBilling={setNotesReadyForBilling}
      setNotesSentForCorrection={setNotesSentForCorrection}
      // Mar 2026 — pass through the hoisted sort state.
      sortColumn={sortColumn}
      setSortColumn={setSortColumn}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      onViewNote={onViewNote}
    />
  );
};

/**
 * Main wrapper component with Error Boundary
 * NOTE: BillingProvider is now at Dashboard level, so we only need Error Boundary here
 */
export const BillingTabWrapper = (props) => {
  return (
    <BillingErrorBoundary>
      <BillingTabContent {...props} />
    </BillingErrorBoundary>
  );
};

export default BillingTabWrapper;
